import { useState } from 'react';
import {
  Grid,
  Paper,
  Box,
  Chip,
  Tooltip,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { capitalizeFirstLetter } from '../components/functions';
import DataHubSidebar, { DataCategory } from '../components/DataHubSidebar';
import DataHubSummary, { DataSummary } from '../components/DataHubSummary';
import DataHubTable from '../components/DataHubTable';
import { DataHubTableToolbar } from '../components/DataHubTableToolbar';
import InfoBoxAlert from '../components/InfoBoxAlert';

// Icons
import StorageIcon from '@mui/icons-material/Storage';
import ImageIcon from '@mui/icons-material/Image';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import TrafficIcon from '@mui/icons-material/Traffic';
import WarningIcon from '@mui/icons-material/Warning';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';

type Dataset = {
  id: number;
  datasetName: string;
  source: string;
  status: string;
  linkedModule: string;
  tags: Tags[];
};

type Tags = 'all-datasets' | 'imagery-lidar' | 'road-geometry' | 'structures' | 'traffic' | 'crash-data' | 'utilities-pup';
// 1. Define permitted status values
type Status = 'validated' | 'in progress' | 'available';

// 2. Correct the statusColors mapping
const statusColors: Record<Status, 'default' | 'success' | 'warning' | 'info'> = {
  // loaded: 'default',
  validated: 'success',
  'in progress': 'warning',
  available: 'info'
};

/**
 * Counts occurrences of each status value in the rows array.
 * @param rows - array of Dataset objects
 * @returns Record where keys are status strings and values are counts
 */
function countStatuses(rows: Dataset[]): Record<string, number> {
  return rows.reduce<Record<string, number>>((acc, row) => {
    acc[row.status] = (acc[row.status] ?? 0) + 1;
    // console.log('acc', acc, row.status, (acc[row.status] ?? 0) + 1)
    return acc;
  }, {});
};

export const dataCategories: DataCategory[] = [
  { id: 1, value: "all-datasets", label: "All datasets", icon: <StorageIcon fontSize='small' /> },
  { id: 2, value: "imagery-lidar", label: "Imagery / LIDAR", icon: <ImageIcon fontSize='small' /> },
  { id: 3, value: "road-geometry", label: "Road geometry", icon: <AltRouteIcon fontSize='small' /> },
  { id: 4, value: "structures", label: "Structures", icon: <LocationCityIcon fontSize='small' /> },
  { id: 5, value: "traffic", label: "Traffic", icon: <TrafficIcon fontSize='small' /> },
  { id: 6, value: "crash-data", label: "Crash data", icon: <WarningIcon fontSize='small' /> },
  { id: 7, value: "utilities-pup", label: "Utilities (PUP)", icon: <ElectricalServicesIcon fontSize='small' /> },
];

export default function DataHubPage() {

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'datasetName',
      headerName: 'DATASET NAME',
      flex: 1,
      editable: true,
    },
    {
      field: 'source',
      headerName: 'SOURCE',
      flex: 1,
      editable: true,
    },
    {
      field: 'status',
      headerName: 'STATUS',
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={capitalizeFirstLetter(params.value)}
          color={statusColors[params.value as Status]}
          variant="outlined"
        />
      )
    },
    {
      field: 'linkedModule',
      headerName: 'LINKED MODULE',
      flex: 1,
    },
    {
      field: 'tags',
      headerName: 'TAGS',
      flex: 1,
      renderCell: (params) => (
        <Tooltip
          title={params.value.join(', ')} // Shows all tags, comma separated
        // arrow
        >
          <Box sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block'
          }}>
            {params.value.map((tag: Tags) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
                sx={{ mr: 0.5 }}
              />
            ))}
          </Box>
        </Tooltip>
      )
    },
  ];

  const rows: Dataset[] = [
    {
      id: 1,
      tags: ['imagery-lidar'],
      datasetName: 'Emerald Corridor DEM (1m)',
      source: 'TMR',
      status: 'validated',
      linkedModule: 'Grades / DEM',
    },
    {
      id: 2,
      tags: ['imagery-lidar', 'road-geometry'],
      datasetName: 'LIDAR Point Cloud - Gregory 27A extent',
      source: 'Survey / TMR',
      status: 'available',
      linkedModule: 'Grades / DEM',
    },
    {
      id: 3,
      tags: ['road-geometry', 'traffic'],
      datasetName: 'Calibrated route centreline (GREG_27A)',
      source: 'RLP/TMR',
      status: 'in progress',
      linkedModule: 'Curvature / Overview',
    },
    {
      id: 4,
      tags: ['road-geometry'],
      datasetName: 'Road Link Plan horizontal geometry',
      source: 'TMR',
      status: 'validated',
      linkedModule: 'Curvature / Overview',
    },
    {
      id: 5,
      tags: ['road-geometry', 'structures'],
      datasetName: 'Formation Widening Treatments',
      source: 'Survey / TMR',
      status: 'in progress',
      linkedModule: 'Overview / Width',
    },
    {
      id: 6,
      tags: ['structures', 'utilities-pup'],
      datasetName: 'Bridges and culverts inventory',
      source: 'TMR',
      status: 'validated',
      linkedModule: 'Grades / DEM',
    },
    {
      id: 7,
      tags: ['traffic'],
      datasetName: 'Heavy vehicle traffic counts (AADT)',
      source: 'TMR',
      status: 'available',
      linkedModule: 'Capacity / Overtaking',
    },
    {
      id: 8,
      tags: ['traffic', 'road-geometry'],
      datasetName: 'Freight demand projections',
      source: 'Survey',
      status: 'in progress',
      linkedModule: 'Safety Screening',
    },
    {
      id: 9,
      tags: ['crash-data', 'traffic'],
      datasetName: 'Crash history',
      source: 'Survey',
      status: 'validated',
      linkedModule: 'Planning',
    },
    {
      id: 10,
      tags: ['road-geometry'],
      datasetName: 'Environmental Constraints Map',
      source: 'EPA',
      status: 'available',
      linkedModule: 'Planning',
    },
    {
      id: 11,
      tags: ['utilities-pup'],
      datasetName: 'Soil Survey Data',
      source: 'Geotech',
      status: 'validated',
      linkedModule: 'Grades / DEM',
    },
    {
      id: 12,
      tags: ['road-geometry'],
      datasetName: 'Land Use Zoning',
      source: 'Council',
      status: 'validated',
      linkedModule: 'Planning',
    },
    {
      id: 13,
      tags: ['structures', 'road-geometry', 'utilities-pup'],
      datasetName: 'Heritage Site Inventory',
      source: 'Council / TMR',
      status: 'validated',
      linkedModule: 'Planning',
    },
    {
      id: 14,
      tags: ['crash-data', 'traffic'],
      datasetName: 'Flood Risk Assessment',
      source: 'TMR',
      status: 'in progress',
      linkedModule: 'Safety Screening',
    },
  ];

  const [selectedCategory, setSelectedCategory] = useState<DataCategory | null>(null);
  const [filteredRows, setFilteredRows] = useState<Dataset[]>(rows); // rows is your full dataset

  const handleCategorySelect = (category: DataCategory) => {
    setSelectedCategory(category);

    if (!category.value || category.value === 'all-datasets') {
      setFilteredRows(rows); // Show all
    } else {
      setFilteredRows(
        rows.filter(row => row.tags.includes(category.value as Tags))
      );
    }
  };
  const dataSummary: DataSummary[] = [
    { id: 1, count: filteredRows.length, label: "Total datasets", icon: <StorageIcon fontSize='small' /> },
    { id: 2, count: countStatuses(filteredRows)['validated'], label: "Validated", icon: <CheckCircleIcon color='success' fontSize='small' /> },
    { id: 3, count: countStatuses(filteredRows)['available'], label: "Available", icon: <CircleIcon color='info' fontSize='small' /> },
    { id: 4, count: countStatuses(filteredRows)['in progress'], label: "In progress", icon: <AccessTimeFilledIcon color='warning' fontSize='small' /> },
  ];
  return (
    <Box>
      {/* MAIN CONTENT */}
      <Grid container>
        {/* SIDEBAR */}
        <Grid
          size={{ xs: 6, md: 2.5 }}
          sx={{
            overflowY: 'auto',
            height: 'calc(100vh - 64px - 24px - 24px - 24.33px)' // screenHeight - navBarHeight - paddingTop - paddingBottom
          }}
        >
          <Paper elevation={1} sx={{ background: "#f6f8ff" }}>
            <Typography variant='body1' sx={{ p: 2, fontWeight: 'bold' }}>DATA CATEGORIES</Typography>
            <DataHubSidebar categories={dataCategories} onCategorySelect={handleCategorySelect} />
          </Paper>
          <br />
          <Paper elevation={1} sx={{ background: "#f6f8ff" }}>
            <Typography variant='body1' sx={{ p: 2, fontWeight: 'bold' }}>DATA SUMMARY</Typography>
            <DataHubSummary categories={dataSummary} />
          </Paper>
        </Grid>

        {/* MAIN AREA */}
        <Grid
          size={{ xs: 6, md: 9.5 }}
          sx={{
            overflowY: 'auto',
            height: 'calc(100vh - 64px - 24px - 24px - 24.33px)' // screenHeight - navBarHeight - paddingTop - paddingBottom
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              height: '100%',
              border: '1px solid #ccc'
            }}
          >
            <InfoBoxAlert icon='information' closeable>
              <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                This Data Hub organises TMR datasets within a GIS- web project model to support interrogation, analysis and extraction across IFR assessment modules. Layers are published to ArcGIS Portal Web Maps - not custom siloed stores.
              </Typography>
            </InfoBoxAlert>
            <DataHubTable rows={filteredRows} columns={columns} showToolbar customToolbar={DataHubTableToolbar} />
          </Paper>
        </Grid>
      </Grid>
    </Box >
  )
}